import { type RefObject, useEffect, useRef, useState } from "react";

export interface Dimension {
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
}

// Browser vendor-prefixed fullscreen API. document.fullscreenElement is the
// standard, but Safari uses webkit- and legacy Edge uses ms-.
interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  msFullscreenElement?: Element | null;
}

// Wrapper around ResizeObserver. Returns [ref, dimensions, dimensionsRef].
export function useResizeObserver(): [
  RefObject<HTMLDivElement | null>,
  Dimension,
  RefObject<Dimension>,
] {
  const ref = useRef<HTMLDivElement | null>(null);
  const dimensionsRef = useRef<Dimension>({
    width: 0,
    height: 0,
    maxWidth: 0,
    maxHeight: 0,
  });
  const [dimensions, setDimensions] = useState<Dimension>({
    width: 0,
    height: 0,
    maxWidth: 0,
    maxHeight: 0,
  });

  useEffect(() => {
    if (!ref.current) return;

    const initialDims = ref.current.getBoundingClientRect();
    setDimensions({
      width: initialDims.width,
      height: initialDims.height,
      maxWidth: initialDims.width,
      maxHeight: initialDims.height,
    });
    dimensionsRef.current = {
      width: initialDims.width,
      height: initialDims.height,
      maxWidth: initialDims.width,
      maxHeight: initialDims.height,
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setDimensions((prev) => ({
          width: cr.width,
          height: cr.height,
          maxWidth: Math.max(cr.width, prev.width),
          maxHeight: Math.max(cr.height, prev.height),
        }));
        dimensionsRef.current = {
          width: cr.width,
          height: cr.height,
          maxWidth: Math.max(cr.width, dimensionsRef.current.maxWidth),
          maxHeight: Math.max(cr.height, dimensionsRef.current.maxHeight),
        };
      }
    });

    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [ref.current]);

  return [ref, dimensions, dimensionsRef];
}

export default useResizeObserver;

export function useDocumentIsFullScreen() {
  const [isDocumentFullScreen, setIsDocumentFullScreen] =
    useState<boolean>(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const d = document as FullscreenDocument;
      setIsDocumentFullScreen(
        !!(d.fullscreenElement ||
          d.webkitFullscreenElement ||
          d.msFullscreenElement),
      );
    };

    handleFullscreenChange();
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  return isDocumentFullScreen;
}

/**
 * Reactive media query matcher — re-renders the consumer when the match
 * state changes. SSR-safe: returns `false` on the first render before the
 * effect runs.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}
